import React from 'react';
import { RecordedDocument } from 'pages/Search/models';
import { useSelector, useDispatch } from 'react-redux';
import {
  Button,
  Descriptions,
  Row,
  Typography,
  Tooltip,
  Divider,
  Checkbox,
} from 'antd';
import {
  AttributeValuePair,
  loginSelector,
  tabSelector,
  getDataTypeName,
} from 'common';
import {
  searchDetailsAdd,
  fetchDetailsAsync,
  fetchExternalDetailsAsync,
  searchIsSelectedUpdate,
  isSelectedSelector,
} from 'pages/Search';
import { RecordedDocumentReferences } from '..';
import { createSelector } from 'reselect';
import { ResultTypeName } from 'pages/Search/Search.types';
import { useAccount, useMsal } from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';
import { getAccessToken } from 'common/helpers';

const { Text } = Typography;

const selector = createSelector(
  loginSelector,
  tabSelector,
  isSelectedSelector,
  (login, tabName, getIsSelected) => ({
    login,
    tabName,
    getIsSelected,
  }),
);

const DocumentsListItem: React.FC<{
  document: RecordedDocument;
  index: number;
  showReferences?: boolean;
  resultType: ResultTypeName;
  resultItemIndex: number;
}> = ({ document, index, showReferences, resultType, resultItemIndex }) => {
  const {
    documentType,
    firstSideOfTransaction,
    secondSideOfTransaction,
    guid,
    remarks,
    documentsIds,
    documentID,
    boro,
    reelFileNumber,
    amount,
    instr,
    dated,
    pageOFileNumber,
    crfn,
    dateRec,
    documentTypeDetails,
    documentReferences,
  } = document;
  const dispatch = useDispatch();
  const { tabName, getIsSelected } = useSelector(selector);
  const { instance, accounts, inProgress } = useMsal();
  const account = useAccount(accounts[0] || {});

  let { firstSideOfTransactionHeaderName, secondSideOfTransactionHeaderName } =
    documentTypeDetails;

  firstSideOfTransactionHeaderName = firstSideOfTransactionHeaderName
    ? firstSideOfTransactionHeaderName
    : 'Party 1 Names';
  secondSideOfTransactionHeaderName = secondSideOfTransactionHeaderName
    ? secondSideOfTransactionHeaderName
    : 'Party 2 Names';

  const onChange = ({ target: { checked } }: any) => {
    dispatch(
      searchIsSelectedUpdate(
        getDataTypeName(tabName),
        documentType,
        guid,
        checked,
        resultItemIndex,
        resultType,
      ),
    );
  };

  const fetchDetails = () => {
    if (account && inProgress === InteractionStatus.None) {
      getAccessToken(instance, account, inProgress).then((response) => {
        if (response?.accessToken)
          dispatch(
            fetchDetailsAsync(
              documentType,
              boro,
              documentID,
              response.accessToken,
            ),
          );
      });
    }
  };

  const fetchExternalDetails = () => {
    if (account && inProgress === InteractionStatus.None) {
      getAccessToken(instance, account, inProgress).then((response) => {
        if (response?.accessToken)
          dispatch(
            fetchExternalDetailsAsync(documentsIds, response.accessToken),
          );
      });
    }
  };

  const isSelected = React.useMemo(() => {
    return getIsSelected(
      getDataTypeName(tabName),
      documentType,
      guid,
      resultItemIndex,
      resultType,
    );
  }, [documentType, getIsSelected, guid, resultItemIndex, resultType, tabName]);

  const boroConvert = (boro: number): string => {
    switch (boro) {
      case 1:
        return 'New York';
      case 2:
        return 'Bronx';
      case 3:
        return 'Kings';
      case 4:
        return 'Queens';
      case 5:
        return 'Richmond';
      default:
        return 'ERROR';
    }
  };

  return (
    <Descriptions
      size="small"
      column={7}
      title={
        <Row type="flex" justify="space-between" align="middle">
          <Text>
            #{index + 1}&nbsp;
            <Tooltip title={'Check for export'}>
              <Checkbox onChange={onChange} checked={isSelected} />
            </Tooltip>
            &nbsp; Instrument: {instr}
          </Text>
          <Button
            type="primary"
            size="small"
            icon="file-search"
            onClick={() => {
              documentType === 'federalTaxLiens'
                ? dispatch(searchDetailsAdd(document))
                : fetchDetails();
            }}
          >
            Details
          </Button>
        </Row>
      }
    >
      <Descriptions.Item>
        <AttributeValuePair
          attribute={'Recorded'}
          value={dateRec}
          valueType="date"
        />
      </Descriptions.Item>

      {documentType === 'recordedDocuments' && (
        <>
          <Descriptions.Item>
            <AttributeValuePair
              attribute={'Reel'}
              value={crfn === '' ? reelFileNumber : ''}
            />
          </Descriptions.Item>
          <Descriptions.Item>
            <AttributeValuePair
              attribute={'Page'}
              value={crfn === '' ? pageOFileNumber : ''}
            />
          </Descriptions.Item>
        </>
      )}
      {(documentType === 'uniformCommercialCodes' ||
        documentType === 'federalTaxLiens') && (
        <>
          <Descriptions.Item>
            <AttributeValuePair attribute={'File Num'} value={reelFileNumber} />
          </Descriptions.Item>
          <Descriptions.Item>
            <AttributeValuePair
              attribute={'Orig Doc'}
              value={pageOFileNumber}
            />
          </Descriptions.Item>
        </>
      )}
      <Descriptions.Item span={2}>
        {firstSideOfTransaction.map((item, index) => {
          return (
            <React.Fragment key={index}>
              {index === 0 ? (
                <AttributeValuePair
                  attribute={firstSideOfTransactionHeaderName}
                  value={`${item.name}`}
                />
              ) : (
                item.name
              )}
              {(documentType === 'uniformCommercialCodes' ||
                documentType === 'federalTaxLiens') && (
                <>
                  <br />
                  {`${item.address}`}
                </>
              )}
              <br />
            </React.Fragment>
          );
        })}
      </Descriptions.Item>
      <Descriptions.Item span={2}>
        {secondSideOfTransaction.map((item, index) => {
          return (
            <React.Fragment key={index}>
              {index === 0 ? (
                <AttributeValuePair
                  attribute={secondSideOfTransactionHeaderName}
                  value={`${item.name}`}
                />
              ) : (
                item.name
              )}
              {(documentType === 'uniformCommercialCodes' ||
                documentType === 'federalTaxLiens') && (
                <>
                  <br />
                  {`${item.address}`}
                </>
              )}
              <br />
            </React.Fragment>
          );
        })}
      </Descriptions.Item>
      {documentsIds && documentsIds.length < 1 ? (
        <Descriptions.Item span={2}>
          <AttributeValuePair attribute={'Document ID'} value={documentID} />
        </Descriptions.Item>
      ) : (
        <Descriptions.Item span={2}>
          <Text strong>{`Document ID:`}</Text>&nbsp;
          <Tooltip title="Document Viewer">
            <Button
              type="link"
              icon="paper-clip"
              onClick={() => {
                fetchExternalDetails();
              }}
            >
              {documentID}
            </Button>
          </Tooltip>
        </Descriptions.Item>
      )}
      <Descriptions.Item span={2}>
        <AttributeValuePair attribute={'CRFN'} value={crfn} />
      </Descriptions.Item>
      <Descriptions.Item span={3}>
        <AttributeValuePair attribute={'County'} value={boroConvert(boro)} />
      </Descriptions.Item>
      {documentType === 'uniformCommercialCodes' ||
      documentType === 'federalTaxLiens' ? (
        <Descriptions.Item span={7}>
          <AttributeValuePair
            attribute={'Amount'}
            value={amount}
            valueType="money"
          />
        </Descriptions.Item>
      ) : (
        amount > 0 && (
          <Descriptions.Item span={7}>
            <AttributeValuePair
              attribute={'Amount'}
              value={amount}
              valueType="money"
            />
          </Descriptions.Item>
        )
      )}

      {documentType === 'recordedDocuments' && (
        <Descriptions.Item span={7}>
          <AttributeValuePair
            attribute={'Dated'}
            value={dated}
            valueType="date"
          />
        </Descriptions.Item>
      )}
      <Descriptions.Item span={7}>
        <Divider dashed />
        <Text strong>{`Remarks:`}</Text>&nbsp;
        {remarks && remarks.length > 0 ? (
          remarks.map((item, index) => (
            <React.Fragment key={index}>
              <Text>{item.remarkText}</Text>
              <br />
            </React.Fragment>
          ))
        ) : (
          <Text>N</Text>
        )}
      </Descriptions.Item>
      {showReferences && documentReferences && documentReferences.length > 0 && (
        <Descriptions.Item span={7}>
          <Divider dashed />
          <Text strong>{`DOC References:`}</Text>&nbsp;
          <RecordedDocumentReferences items={documentReferences} />
        </Descriptions.Item>
      )}
    </Descriptions>
  );
};

export default React.memo(DocumentsListItem);
